import { BarChart3, Users, Calendar, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@lean-agility/design-system/react';
import { IconContainer } from '@/components/atoms';
import type { ProcessedDeliverableResult } from '@/types/api';

export interface ResultsSummaryProps {
  results: ProcessedDeliverableResult[];
  totalSimulations: number;
}

export function ResultsSummary({ results, totalSimulations }: ResultsSummaryProps) {
  const uniqueTeams = new Set(results.map((r) => r.team_name)).size;
  const atRiskCount = results.filter((r) => r.atRisk).length;
  const withDueDates = results.filter((r) => r.due_date).length;

  // Find the latest p85 date across all deliverables
  const latestP85 = results.reduce(
    (latest, r) => {
      const p85 = r.finishPercentiles.p85;
      if (!p85) return latest;
      if (!latest || p85 > latest) return p85;
      return latest;
    },
    null as string | null,
  );

  const stats = [
    {
      label: 'Simulations Run',
      value: totalSimulations.toLocaleString(),
      icon: BarChart3,
      color: 'text-action-primary',
      variant: 'accent' as const,
      highlight: false,
    },
    {
      label: 'Teams',
      value: uniqueTeams,
      icon: Users,
      color: 'text-feedback-info',
      variant: 'info' as const,
      highlight: false,
    },
    {
      label: 'Deliverables',
      value: results.length,
      icon: Calendar,
      color: 'text-feedback-success',
      variant: 'success' as const,
      highlight: false,
    },
    {
      label: 'Due Dates At Risk',
      value: `${atRiskCount}/${withDueDates}`,
      icon: AlertTriangle,
      color: atRiskCount > 0 ? 'text-feedback-error' : 'text-feedback-success',
      variant: atRiskCount > 0 ? ('error' as const) : ('success' as const),
      highlight: atRiskCount > 0,
    },
  ];

  return (
    <div className="animate-fade-in space-y-4">
      {/* Stats Grid */}
      <div
        className="
          grid grid-cols-2 gap-4
          md:grid-cols-4
        "
      >
        {stats.map((stat) => (
          <Card
            key={stat.label}
            elevation="soft"
            className={stat.highlight ? `border-feedback-error/30 bg-feedback-error/10` : ''}
          >
            <CardContent className="flex items-center justify-center gap-4 py-4">
              <IconContainer variant={stat.variant} size="xl">
                <stat.icon
                  className={`
                    size-8
                    ${stat.color}
                  `}
                />
              </IconContainer>
              <div>
                <p
                  className={`
                    text-4xl font-bold tracking-tight
                    ${stat.highlight ? `text-feedback-error` : `text-text-primary`}
                  `}
                >
                  {stat.value}
                </p>
                <p
                  className={`
                    text-sm font-medium
                    ${stat.highlight ? `text-feedback-error` : `text-text-secondary`}
                  `}
                >
                  {stat.label}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall Timeline */}
      {latestP85 && (
        <Card elevation="raised" className="bg-linear-to-r from-bg-elevated to-bg-subtle">
          <CardContent className="py-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">All deliverables complete by (85% confidence)</p>
                <p className="mt-1 text-2xl font-bold tracking-tight text-text-primary">
                  {new Date(latestP85).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-muted">Based on {totalSimulations.toLocaleString()} forecast runs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
